import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../../TokenHelper';
import { ConsultantDocument } from '../../interfaces/Consultant';
import { TeamsState } from '../../store/TeamsState';
import { useEditorStyles } from '../Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Label, Link, Switch, SwitchOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { ConsultantActionCreators } from '../../store/consultants';
import { useTranslation } from 'react-i18next';
import { dataActionCreators } from '../../store/Data';
import { useCustomers } from '../Tools/ObjectLoader';
import { DataHelpers } from '../../DataHelpers';

export const InvoiceDeleteConfirm = () => {
    // All data needed for edition should be loaded before calling the editor
    const invoiceToDelete = useSelector((state: ApplicationState) => state.data.invoiceToDelete);
    const { customers } = useCustomers();
    const c = React.useMemo(() =>
        invoiceToDelete ? DataHelpers.GetCustomerFromId(customers, invoiceToDelete.customerid) : null
        , [invoiceToDelete, customers]);

    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    const { t } = useTranslation();
    // Form validation
    const [confirmDelete, setConfirmDelete] = React.useState(false);

    if (invoiceToDelete === null) {
        return null;
    }

    const submit = () => {
        TokenHelper.GetToken(instance, teamsState)
            .then(token => {
                if (token !== null) {
                    const toastId = (Math.random() + 1).toString(36).substring(7);
                    dispatchToast(
                        <Toast appearance="inverted">
                            <ToastTitle>
                                Deleting Invoice...
                            </ToastTitle>
                        </Toast>,
                        { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                    );
                    fetch("api/Invoices/" + (invoiceToDelete && invoiceToDelete.invoiceid !== null ? invoiceToDelete.invoiceid : ""), {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            authorization: "bearer " + token,
                        },
                        mode: "cors",
                        cache: "default",
                    })
                        .then(response => {
                            if (response.status === 204) {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Invoice deleted
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "success",
                                    toastId,
                                    timeout: 5000,
                                });
                                if (invoiceToDelete) dispatch(dataActionCreators.patchInvoiceDeletionEntryEdition(invoiceToDelete));
                                dispatch(dataActionCreators.requestInvoiceDeletion(null));
                            }
                            else {
                                updateToast({
                                    content: (
                                        <Toast appearance="inverted">
                                            <ToastTitle action={
                                                <ToastTrigger>
                                                    <Link>Dismiss</Link>
                                                </ToastTrigger>
                                            }>
                                                Invoice delete failed
                                            </ToastTitle>
                                        </Toast>
                                    ),
                                    intent: "error",
                                    toastId,
                                    timeout: 10000,
                                });
                            }
                        });
                }
            });
    };

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>Delete invoice</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestInvoiceDeletion(null));
                        }}
                        autoComplete="off"
                    >
                        <Field label="Date">
                            <Label className={styles.label}>{invoiceToDelete.invoiceDate ? new Date(invoiceToDelete.invoiceDate).toLocaleDateString() : "-"}</Label>
                        </Field>
                        <Field label="Source">
                            <Label className={styles.label}>{c ? c.displayName : ""}</Label>
                        </Field>
                        <Field label="Description">
                            <Label className={styles.label}>{invoiceToDelete.description !== null ? invoiceToDelete.description : ""}</Label>
                        </Field>
                        <Switch checked={confirmDelete} label="Confirm deletion" onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setConfirmDelete(data.checked); }} />
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestInvoiceDeletion(null))}
                    >
                        {t("Cancel")}
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={!confirmDelete}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};